import { useCallback, useContext, useMemo, useState } from "react"
import { uniqueId } from "lodash"
import { AdminContext } from "~/contexts/AdminContext"
import api from "~/api2"

interface ImageState {
  file: File | null
  url: string
  key: string
  isLoading: boolean
}

export type CampImagesType = "logo" | "map" | "camp-photos"

interface UseCampImagesReturn {
  imageStates: ImageState[]
  handleAddCampImages: (files: FileList | File, type?: CampImagesType) => void
  handleRemoveCampImage: (key: string, type?: CampImagesType) => void
  fetchCampImages: (type?: CampImagesType) => void
  updateImageLoadState: (key: string, isLoading: boolean) => void
  logo: ImageState | null
  map: ImageState | null
}
export const useCampImageUploader = (camp: { id: string }): UseCampImagesReturn => {
  const [imageStates, setImageStates] = useState<ImageState[]>([])
  const [logo, setLogo] = useState<ImageState | null>(null)
  const [map, setMap] = useState<ImageState | null>(null)
  const { showNotification } = useContext(AdminContext)

  const handleAddCampImages = useCallback(
    async (files: FileList | File, type: CampImagesType = "camp-photos") => {
      if (type === "camp-photos") {
        const fileList = Array.from(files as FileList)
        const newImages = fileList.map((file) => ({
          file,
          url: URL.createObjectURL(file),
          key: `temp-key-${uniqueId()}`,
          isLoading: true,
        }))

        setImageStates((prevStates) => prevStates.concat(newImages))

        for (const imageState of newImages) {
          try {
            const response = await api.Camps.postImages(camp.id, imageState.file, "camp-photos")
            setImageStates(response.images.map((image) => ({ ...image, file: null, isLoading: false })))
          } catch (err: any) {
            console.error(err)
            showNotification({
              type: "error",
              title: `Failed to upload camp-photo ${imageState.file.name}`,
              description: err.message,
            })
            setImageStates((currentStates) =>
              currentStates.map((img) => (img.key === imageState.key ? { ...img, isLoading: false } : img)),
            )
          }
        }
      } else {
        const setState = type === "map" ? setMap : setLogo
        setState({ file: null, url: "", key: "", isLoading: true })

        try {
          await api.Camps.postImages(camp.id, files as File, type)
        } catch (err: any) {
          showNotification({
            type: "error",
            title: `Failed to upload ${type} ${(files as File).name}`,
            description: err.message,
          })
        } finally {
          fetchCampImages(type)
        }
      }
    },
    [camp.id, showNotification],
  )

  const handleRemoveCampImage = useCallback(
    async (key: string, type: CampImagesType = "camp-photos") => {
      if (type === "camp-photos") {
        setImageStates((currentStates) =>
          currentStates.map((img) => (img.key === key ? { ...img, isLoading: true } : img)),
        )

        try {
          await api.Camps.deleteImages(camp.id, "camp-photos", key.split("/")[3])
          setImageStates((currentStates) => currentStates.filter((img) => img.key !== key))
          showNotification({
            type: "success",
            title: "Camp photo removed",
            description: "The camp photo was successfully removed.",
          })
        } catch (err: any) {
          console.error(err)
          showNotification({
            type: "error",
            title: "Failed to remove image",
            description: err.message,
          })
          setImageStates((currentStates) =>
            currentStates.map((img) => (img.key === key ? { ...img, isLoading: false } : img)),
          )
        }
      } else {
        try {
          await api.Camps.deleteImages(camp.id, type)
          if (type === "logo") setLogo(null)
          else setMap(null)
          showNotification({
            type: "success",
            title: `${type.charAt(0).toUpperCase() + type.slice(1)} removed`,
            description: `The camp ${type} was successfully removed.`,
          })
        } catch (err: any) {
          console.error(err)
          showNotification({
            type: "error",
            title: `Failed to remove ${type}`,
            description: err.message,
          })
        }
      }
    },
    [camp.id, showNotification],
  )

  const fetchCampImages = useCallback(
    (type?: CampImagesType) => {
      if (!camp) return

      if (type === "camp-photos") {
        api.Camps.fetchImages(camp.id, "camp-photos")
          .then((response) => {
            if (response) {
              setImageStates(response.images.map((image) => ({ ...image, file: null, isLoading: false })))
            }
          })
          .catch((err) => {
            console.error(err)
          })
          .finally(() => {
            setImageStates((currentStates) =>
              currentStates.map((img) => ({
                ...img,
                isLoading: false,
              })),
            )
          })
      } else {
        api.Camps.fetchImages(camp.id, type)
          .then((response) => {
            if (response) {
              const setState = type === "map" ? setMap : setLogo
              setState(response.images.map((image) => ({ ...image, file: null, isLoading: false }))[0])
            }
          })
          .catch((err) => {
            console.error(err)
          })
          .finally(() => {
            const setState = type === "map" ? setMap : setLogo
            setState((prevState) => (prevState ? { ...prevState, isLoading: false } : null))
          })
      }
    },
    [camp],
  )

  const updateImageLoadState = useCallback((key: string, isLoading: boolean) => {
    setImageStates((currentStates) => currentStates.map((img) => (img.key === key ? { ...img, isLoading } : img)))
  }, [])

  return useMemo(
    () => ({
      fetchCampImages,
      handleAddCampImages,
      handleRemoveCampImage,
      updateImageLoadState,
      logo,
      map,
      imageStates,
    }),
    [fetchCampImages, handleAddCampImages, handleRemoveCampImage, updateImageLoadState, logo, map, imageStates],
  )
}
